import React, {Fragment} from 'react';
import {LogoBox} from "../user-menu/logo-box/logo-box.component";
import {observer} from 'mobx-react';
import {Button} from '../../basic-ui-components/button/base/button.component';
import {OrbitComponent} from '../base-components/orbit-component/orbit-component';
import {GameSearchComponent} from '../game-search/game-search.component';
import {HeaderConfiguration} from '../../services/configurations/config-data-sections/header.configuration';
import {HelpButtonComponent} from './top-header-right/help-button/help-button.component';
import {AppHeaderCrossBrandsComponent} from './top-header-left/cross-brand/app-header-cross-brands.component';
import {RegulatoryDataComponent} from "./top-header-left/regulatory-data/regulatory-data.component";
import {MobileBurgerMenuComponent} from './top-header-left/mobile-burger-menu/mobile-burger-menu.component';
import {HeaderWelcomeButtonsComponent} from './welcome-buttons/header-welcome-buttons.component';
import {HeaderLanguageSwitcherComponent} from './top-header-right/language-switcher/header-language-switcher.component';
import {InsideOrbitMenuContainerComponent} from "../inside-orbit-menu-container/inside-orbit-menu-container.component";
import {DepositHeaderButtonComponent} from "./top-header-right/deposit/deposit-header-button.component";
import {NavbarSeparatorBox} from "./boxes/navbar-separator/navbar-separator.box";
import {HeaderNavbarContainerBox} from "./boxes/header-navbar-container/header-navbar-container.box";
import {HeaderNavbarLeftContainerBox} from "./boxes/navbar-left-container/header-navbar-left-container.box";
import {HeaderNavbarRightContainerBox} from "./boxes/navbar-right-container/header-navbar-right-container.box";
import {Image} from "../raw-components/image/image";
import {StateSelectorButtonComponent} from "./top-header-right/state-selector-button/state-selector-button.component";
import {AreaType, ControlCenterOpenSource} from '@sparkware/uc-sdk-core';
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from '../../../common/const/casinofied-analytics-consts';
import {SearchIcon} from "../../themes/defaults/icons/search.icon";
import {
    TOOLTIP_TARGET_CONTROL_CENTER_PC_BUTTON
} from "../../services/tooltip/on-boarding-tooltip/on-boarding-tooltip-target-selectors";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {withServices} from "../../react-hooks/with-services";

interface HeaderComponentProps extends ComponentServicesPick<'header'> {

}

@observer
export class ComponentBody extends OrbitComponent<HeaderComponentProps> {

    private get _headerConfig(): HeaderConfiguration {
        return this.services.configuration.data.header;
    }

    private _captureHeaderRef = (element: HTMLElement | null) => {
        this.props.header.setHeaderElement(element || null);
    };

    private _renderInsideOrbitMenuContainerPC() {
        return <InsideOrbitMenuContainerComponent />;
    }

    private _renderDesktopSearchInput() {

        if (this._headerConfig?.header_buttons?.search?.display !== true) {
            return null;
        }

        return <GameSearchComponent />;
    }

    private _renderDesktopControlCenterButton() {

        if (!this.services.controlCenter.isEnabled) {
            return null;
        }

        return (
            <Button
                preset="flatStroked"
                icon={{Component: this.services.theme.currentTheme.icons.controlCenterIcon}}
                onClick={async () => {
                    (await this.services.userMenu).closeMenu();
                    await (await this.services.appLauncher).openControlCenterFull({
                        businessCorrelationID: this.services.tools.getGuid(),
                        channel: {
                            area: AreaType.Header,
                            element: CHANNEL_ELEMENTS.controlCenterHeaderButton,
                            source: CHANNEL_SOURCE.casinoClient
                        },
                        appSpecificParameters: {
                            source: ControlCenterOpenSource.Lobby
                        }
                    });
                }}
                margin="0 0.4rem"
                tooltip={this.services.translation.translate('SAFE_GAMBLING_TOOLTIP')}
                className={TOOLTIP_TARGET_CONTROL_CENTER_PC_BUTTON}
            />
        );
    }

    private _renderDesktopHelpButton() {
        if (this._headerConfig?.header_buttons?.help?.display !== true) {
            return null;
        }

        return <HelpButtonComponent />;
    }

    private _renderDesktopStateSelectorButton() {

        if (!this.services.stateOfResidenceIdentifier.shouldDisplayStateSelectorButton()) {
            return null;
        }

        return <StateSelectorButtonComponent />;
    }

    private _renderDesktopLanguageSwitcher() {
        if (this._headerConfig?.header_buttons?.languages?.display !== true || this.services.language.languageSwitcher.isEmpty) {
            return null;
        }

        return <HeaderLanguageSwitcherComponent />;
    }

    private _handleSearchClick = async () => {
        this.services.analytics.sendOrbitUserFlowEvent('Navigation', 'NavBar', 'Search_Open');
        await (await this.services.gameSearch).openSearchResult();
    }

    private _renderMobileSearchButton() {
        if (!this.services.abTestingFeatureDataProvider.mobileHeaderSearchButtonEnabled) {
            return null;
        }

        return <>
            <NavbarSeparatorBox className="cy-navbar-separator" />
            <div className={"cy-mobile-header-search-button"} onClick={this._handleSearchClick}>
                <SearchIcon />
            </div>
        </>
    }

    private _renderMobileNavbar() {
        // Do not wrap these 3 components (or a combination of 2) into a wrapper. This will break sticky positioning
        // RegulatoryDataComponent and HeaderNavbarContainerBox have sticky positioning while CrossBrandsComponent if it gets rendered has not
        // CrossBrandsComponent is used here only for MrGreen
        return (
            <>
                <RegulatoryDataComponent attachToHeaderService={true} />
                <AppHeaderCrossBrandsComponent />
                <HeaderNavbarContainerBox className="cy-navbar-container"
                    stickyOffset={this.props.header.headerRegulationHeight}
                    ref={this._captureHeaderRef}
                    useSafeAreaTop={false}>
                    <HeaderNavbarLeftContainerBox className="cy-navbar-left-container">
                        <MobileBurgerMenuComponent />
                        <NavbarSeparatorBox className="cy-navbar-separator" />
                        <LogoBox withAnalytics={true} />
                        {this._renderMobileSearchButton()}
                    </HeaderNavbarLeftContainerBox>
                    <HeaderNavbarRightContainerBox className="cy-navbar-right-container">
                        {this._renderDeposit()}
                        <HeaderWelcomeButtonsComponent />
                    </HeaderNavbarRightContainerBox>
                </HeaderNavbarContainerBox>
            </>
        );
    }

    private _renderSecondLogo() {
        if (!this._headerConfig?.secondaryLogoUrl) {
            return null;
        }

        return (
            <Image src={this._headerConfig?.secondaryLogoUrl} />
        );
    }

    private _renderRelevantComponent(componentIDs: string[]) {
        let elements: Array<{component: React.ReactElement | null; componentID: string}> = [];
        if (!componentIDs) {
            return null;
        }

        for (let index = 0; index < componentIDs.length; index++) {
            const currentComponentID = componentIDs[index];
            switch (currentComponentID) {
                case "app-language-switcher":
                    elements.push({
                        component: this._renderDesktopLanguageSwitcher(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-help-button":
                    elements.push({
                        component: this._renderDesktopHelpButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-safezone":
                    elements.push({
                        component: this._renderDesktopControlCenterButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "state-selection-button":
                    elements.push({
                        component: this._renderDesktopStateSelectorButton(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-responsible-icons":
                    elements.push({
                        component: this._renderResponsibleIcons(),
                        componentID: currentComponentID
                    });
                    break;
                case "app-secondLogo":
                    elements.push({
                        component: this._renderSecondLogo(),
                        componentID: currentComponentID
                    });
                    break;
                case "search-box-form":
                    elements.push({
                        component: this._renderDesktopSearchInput(),
                        componentID: currentComponentID
                    });
                    break;
                case "state-cross-brand":
                    elements.push({
                        component: this._renderCrossBrand(),
                        componentID: currentComponentID
                    });
                    break;
            }
        }

        elements = elements.filter(function (el) {
            return el.component != null;
        });

        if (elements.length === 0) {
            return null;
        }

        return (
            elements.map((element, index) => {
                return (
                    <Fragment key={element.componentID}>
                        {element.component}
                        {index !== (elements.length - 1) && <NavbarSeparatorBox />}
                    </Fragment>
                )
            })
        );
    }

    private _renderResponsibleIcons() {
        if (!this._headerConfig?.responsibleIcons?.display === true) {
            return null;
        }
        return <RegulatoryDataComponent attachToHeaderService={false} />;
    }

    private _renderDesktopLeftNavbar() {
        return (
            <HeaderNavbarLeftContainerBox className="cy-navbar-left-container">
                {this._renderRelevantComponent(this._headerConfig?.top_header_left)}
            </HeaderNavbarLeftContainerBox>
        );
    }

    private _renderCrossBrand() {
        return <AppHeaderCrossBrandsComponent />;
    }

    private _renderDeposit() {
        if (!this.services.user.isAuthorized) {
            return null;
        }

        if (!this.services.mediaQuery.sAndBelow) {
            return null;
        }

        let depositHeaderButtonEnabledFromConfig = this._headerConfig?.header_buttons?.deposit === true;

        if (depositHeaderButtonEnabledFromConfig) {
            return <DepositHeaderButtonComponent />;
        }

        return null;
    }

    private _renderDesktopRightNavbar() {
        return (
            <HeaderNavbarRightContainerBox className="cy-navbar-right-container">
                {this._renderInsideOrbitMenuContainerPC()}
                {this._renderRelevantComponent(this._headerConfig?.top_header_right)}

                <HeaderWelcomeButtonsComponent />
            </HeaderNavbarRightContainerBox>
        );
    }

    render() {
        setTimeout(() => {
            this.props.header.deactivateSkeleton();
        }, 1500);

        if (this.services.device.isMobile) {
            return this._renderMobileNavbar();
        }

        return (
            <HeaderNavbarContainerBox className="cy-navbar-container"
                stickyOffset={0}
                ref={this._captureHeaderRef}
                useSafeAreaTop={true}>
                {this._renderDesktopLeftNavbar()}
                {this._renderDesktopRightNavbar()}
            </HeaderNavbarContainerBox>

        );
    }
}


export const HeaderComponent = withServices(ComponentBody, ['header'])